import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useUsers } from "@/hooks/useUsers";
import {
  ORIGINATION_SOURCE_TYPES, ORIGINATION_SOURCE_LABELS, originationPointsAtContact,
  type OriginationInput, type OriginationSourceType,
} from "@shared/originationConstants";

interface Option { id: string; name: string; companyName?: string | null }

/**
 * The one control that edits an origination row. Used by the Add Prospect
 * form (default: partner network, you), the Source card's edit state, the
 * bulk Set source dialog and the Convert to Client nudge.
 *
 * `locked` renders the type and person read-only: "New prospect from this
 * COI" opens the form with the source already decided.
 */
export function SourceControl({ value, onChange, locked, currentUserId }: {
  value: OriginationInput | null;
  onChange: (v: OriginationInput) => void;
  locked?: { sourceType: OriginationSourceType; contactId: string; contactName: string; firmName: string | null } | null;
  currentUserId?: string | null;
}) {
  const v: OriginationInput = value ?? { sourceType: "partner_network", userId: currentUserId ?? null };
  // A blank value becomes the default the first time the control mounts, so
  // a form that never touches it still submits a valid row.
  useEffect(() => { if (!value) onChange(v); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const set = (patch: Partial<OriginationInput>) => onChange({ ...v, ...patch });
  const { data: users = [] } = useUsers();
  const [q, setQ] = useState("");

  const { data: cois = [] } = useQuery<Option[]>({
    queryKey: ["cois", "people"],
    queryFn: async () => {
      const r = await fetch("/api/cois", { credentials: "include" });
      const rows = r.ok ? await r.json() : [];
      return rows.map((c: any) => ({ id: c.id, name: c.name, companyName: c.firmName }));
    },
    enabled: v.sourceType === "coi_referral" && !locked,
  });
  const { data: lookup = [] } = useQuery<Option[]>({
    queryKey: ["contacts-lookup", v.sourceType, q],
    queryFn: async () => {
      const kind = v.sourceType === "advocate_referral" ? "advocates" : "buyers";
      const r = await fetch(`/api/contacts/lookup?kind=${kind}&q=${encodeURIComponent(q)}`, { credentials: "include" });
      return r.ok ? r.json() : [];
    },
    enabled: (v.sourceType === "advocate_referral" || v.sourceType === "buyer_intro") && !locked,
  });
  const { data: lists = [] } = useQuery<Option[]>({
    queryKey: ["outreach-lists"],
    queryFn: async () => {
      const r = await fetch("/api/outreach/lists", { credentials: "include" });
      const rows: any[] = r.ok ? await r.json() : []; // a plain array of list rows
      return rows.map((l) => ({ id: l.id, name: l.name }));
    },
    enabled: v.sourceType === "outbound",
  });

  const personOptions: Option[] = v.sourceType === "coi_referral" ? cois : lookup;

  return (
    <div className="space-y-3">
      <div className="space-y-1">
        <Label className="text-xs">Source</Label>
        {locked ? (
          <div className="text-sm">
            <span className="font-medium">{ORIGINATION_SOURCE_LABELS[locked.sourceType]}</span>
            <span className="text-muted-foreground"> · {locked.contactName}{locked.firmName ? ` · ${locked.firmName}` : ""}</span>
          </div>
        ) : (
          <Select
            value={v.sourceType}
            onValueChange={(t) => set({
              sourceType: t as OriginationSourceType,
              contactId: null,
              userId: t === "partner_network" ? (v.userId ?? currentUserId ?? null) : null,
              outreachListId: null,
              detail: t === "outbound" ? "research" : null,
            })}
          >
            <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
            <SelectContent>
              {ORIGINATION_SOURCE_TYPES.map((t) => <SelectItem key={t} value={t}>{ORIGINATION_SOURCE_LABELS[t]}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
      </div>

      {!locked && originationPointsAtContact(v.sourceType) && (
        <div className="space-y-1">
          <Label className="text-xs">{v.sourceType === "coi_referral" ? "Which COI" : v.sourceType === "advocate_referral" ? "Which advocate" : "Which buyer contact"}</Label>
          {v.sourceType !== "coi_referral" && (
            <Input className="h-9" placeholder="Type a name to search" value={q} onChange={(e) => setQ(e.target.value)} />
          )}
          <Select value={v.contactId ?? ""} onValueChange={(id) => set({ contactId: id })}>
            <SelectTrigger className="h-9"><SelectValue placeholder="Pick a person" /></SelectTrigger>
            <SelectContent>
              {personOptions.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}{o.companyName ? ` · ${o.companyName}` : ""}</SelectItem>)}
              {personOptions.length === 0 && (
                <div className="px-2 py-1.5 text-xs text-muted-foreground">{v.sourceType === "coi_referral" ? "No COIs yet" : "No matches"}</div>
              )}
            </SelectContent>
          </Select>
        </div>
      )}

      {v.sourceType === "partner_network" && (
        <div className="space-y-1">
          <Label className="text-xs">Whose network</Label>
          <Select value={v.userId ?? ""} onValueChange={(id) => set({ userId: id })}>
            <SelectTrigger className="h-9"><SelectValue placeholder="Pick a partner" /></SelectTrigger>
            <SelectContent>{users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name ?? u.email}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      )}

      {v.sourceType === "outbound" && (
        <div className="space-y-1">
          <Label className="text-xs">Outreach list</Label>
          <Select
            value={v.outreachListId ?? "__research__"}
            onValueChange={(id) => set(id === "__research__" ? { outreachListId: null, detail: "research" } : { outreachListId: id, detail: null })}
          >
            <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__research__">Research (no list)</SelectItem>
              {lists.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      )}

      {(v.sourceType === "event" || v.sourceType === "other") && (
        <div className="space-y-1">
          <Label className="text-xs">{v.sourceType === "event" ? "Event" : "What"}</Label>
          <Input className="h-9" value={v.detail ?? ""} onChange={(e) => set({ detail: e.target.value })} placeholder={v.sourceType === "event" ? "e.g. FTC Q2 owner event" : "A few words"} />
        </div>
      )}

      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1">
          <Label className="text-xs">When</Label>
          <Input type="date" className="h-9" value={v.occurredOn ?? ""} onChange={(e) => set({ occurredOn: e.target.value || null })} />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Note</Label>
          <Input className="h-9" value={v.note ?? ""} onChange={(e) => set({ note: e.target.value })} placeholder="Met at the Chamber lunch" />
        </div>
      </div>
    </div>
  );
}
